import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import NotificationPopup from "./../../views/pages/hms/components/popups/notificationPopup";

// --------------------
// Helpers
// --------------------
const normalizeMod = (m) =>
  String(m ?? "").trim().toLowerCase().replace(/_/g, "-");

const safeJsonParse = (s, fallback) => {
  try {
    const v = JSON.parse(s);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

const pathMatchesBase = (currentPath, basePath) => {
  if (!basePath) return false;
  return currentPath === basePath || currentPath.startsWith(basePath + "/");
};

const startsWithAny = (path, prefixes) =>
  prefixes.some((p) => path === p || path.startsWith(p + "/"));

// --------------------
// Access model
// --------------------
const moduleRoutes = {
  hms: ["/hospitality"],
  pos: ["/pos"],
  feedback: ["/feedback"],
  "digital-menu": ["/digital-menu"],
  "booking-engine": ["/booking-engine"],
  "channel-manager": ["/channel-manager"],
  "table-flow": ["/table-flow"],
};

const modulePrefixes = [
  "/hospitality",
  "/pos",
  "/feedback",
  "/digital-menu",
  "/booking-engine",
  "/channel-manager",
  "/table-flow",
];

const publicPrefixes = ["/login", "/signup", "/forgot-password"];

// Global protected routes (any logged-in user with any access)
const globalProtectedPrefixes = ["/manage-property", "/account-setting"];

const preferLandingOrder = [
  "/hospitality",
  "/pos",
  "/booking-engine",
  "/table-flow",
  "/feedback",
  "/digital-menu",
  "/channel-manager",
];

function AccessDeniedScreen({ message }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "min(560px, 100%)", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.12)", padding: 18, border: "1px solid #eee" }}>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Access denied</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{message}</div>
        <div style={{ fontSize: 12, opacity: 0.7 }}>Please contact your administrator to request access.</div>
      </div>
    </div>
  );
}

const PrivateRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const token = localStorage.getItem("token") || "";
  const rawAccess = safeJsonParse(localStorage.getItem("app_access") || "[]", []);

  const appAccess = useMemo(
    () => (Array.isArray(rawAccess) ? rawAccess.map(normalizeMod) : []),
    [rawAccess]
  );

  const [showNoAccess, setShowNoAccess] = useState(false);
  const [noAccessMsg, setNoAccessMsg] = useState("You don’t have permission to access this page.");

  const authStatus = useMemo(() => {
    if (!token) return { ok: false, reason: "no_token" };
    try {
      const decoded = jwtDecode(token);
      const exp = decoded?.exp;
      if (!exp) return { ok: false, reason: "invalid_token" };
      if (exp * 1000 < Date.now()) return { ok: false, reason: "session_expired" };
      return { ok: true, reason: null };
    } catch {
      return { ok: false, reason: "invalid_token" };
    }
  }, [token]);

  const allowedBasePaths = useMemo(() => {
    return appAccess
      .filter((mod) => moduleRoutes[mod])
      .flatMap((mod) => moduleRoutes[mod]);
  }, [appAccess]);

  const hasAnyAccess = useMemo(() => {
    return appAccess.includes("office") || allowedBasePaths.length > 0;
  }, [appAccess, allowedBasePaths]);

  const isPublicRoute = useMemo(() => startsWithAny(currentPath, publicPrefixes), [currentPath]);

  const isGlobalProtectedRoute = useMemo(
    () => startsWithAny(currentPath, globalProtectedPrefixes),
    [currentPath]
  );

  const isModulePath = useMemo(() => startsWithAny(currentPath, modulePrefixes), [currentPath]);

  const hasOfficeRootAccess = useMemo(() => {
    return appAccess.includes("office") && !isModulePath && !isPublicRoute;
  }, [appAccess, isModulePath, isPublicRoute]);

  const hasModuleAccess = useMemo(() => {
    if (!isModulePath) return false;
    return allowedBasePaths.some((basePath) => pathMatchesBase(currentPath, basePath));
  }, [allowedBasePaths, currentPath, isModulePath]);

  const hasGlobalAccess = useMemo(() => {
    if (!isGlobalProtectedRoute) return false;
    return hasAnyAccess;
  }, [isGlobalProtectedRoute, hasAnyAccess]);

  const hasAccess = useMemo(() => {
    if (!authStatus.ok) return false;
    if (isPublicRoute) return true;
    if (!hasAnyAccess) return false;
    if (hasGlobalAccess) return true;
    return hasOfficeRootAccess || hasModuleAccess;
  }, [authStatus.ok, isPublicRoute, hasAnyAccess, hasGlobalAccess, hasOfficeRootAccess, hasModuleAccess]);

  const firstAllowedBasePath = useMemo(() => {
    for (const p of preferLandingOrder) {
      if (allowedBasePaths.includes(p)) return p;
    }
    return allowedBasePaths[0] || null;
  }, [allowedBasePaths]);

  // ✅ Decide where to land if denied
  const fallbackPath = useMemo(() => {
    // If office-only user and no module access, send to office landing
    if (appAccess.includes("office") && !firstAllowedBasePath) return "/employee-dashboard";
    return firstAllowedBasePath || "/login";
  }, [appAccess, firstAllowedBasePath]);

  // ✅ Show popup if we arrived here due to redirect from denied route
  useEffect(() => {
    const deniedState = location.state?.deniedFrom;
    const msg = location.state?.noAccessMsg;

    if (deniedState && msg) {
      setNoAccessMsg(msg);
      setShowNoAccess(true);

      // Clear state so refresh/back doesn't re-trigger forever
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Normal popup control when staying on same page (optional)
  useEffect(() => {
    if (isPublicRoute) {
      setShowNoAccess(false);
      return;
    }
    if (!authStatus.ok || !hasAnyAccess) {
      setShowNoAccess(false);
      return;
    }
    if (hasAccess) {
      // do not auto-hide if it was triggered by denied redirect; user closes it
      return;
    }
    setNoAccessMsg("You don’t have permission to access this page.");
    setShowNoAccess(true);
  }, [authStatus.ok, hasAnyAccess, hasAccess, isPublicRoute, currentPath]);

  // --------------------
  // Returns
  // --------------------
  if (!authStatus.ok) {
    if (authStatus.reason === "invalid_token" || authStatus.reason === "session_expired") {
      localStorage.removeItem("token");
      localStorage.removeItem("app_access");
    }
    const q =
      authStatus.reason === "session_expired"
        ? "session_expired"
        : authStatus.reason === "invalid_token"
        ? "invalid_token"
        : null;

    return <Navigate to={q ? `/login?error=${q}` : "/login"} replace />;
  }

  if (!hasAnyAccess) return <Navigate to="/login" replace />;

  // ✅ If denied, redirect to fallback but carry popup state
  if (!hasAccess) {
    const msg = "You don’t have permission to access this page.";
    if (fallbackPath && currentPath !== fallbackPath) {
      return (
        <Navigate
          to={fallbackPath}
          replace
          state={{ deniedFrom: currentPath, noAccessMsg: msg }}
        />
      );
    }

    // If for some reason fallback == current, show denied screen + popup
    return (
      <>
        <AccessDeniedScreen message={noAccessMsg} />
        {showNoAccess && (
          <NotificationPopup
            show={true}
            message={noAccessMsg}
            onClose={() => setShowNoAccess(false)}
          />
        )}
      </>
    );
  }

  // ✅ Allowed: render children + popup if any
  return (
    <>
      <Outlet />
      {showNoAccess && !isPublicRoute && (
        <NotificationPopup
          show={true}
          message={noAccessMsg}
          onClose={() => setShowNoAccess(false)}
        />
      )}
    </>
  );
};

export default PrivateRoute;